import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Gavel, ChatCircle, ListChecks, CalendarBlank, UsersThree, MapPin, ArrowRight,
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchAgenda, type AgendaItem, type AgendaItemType } from '@/lib/meApi';
import { useWebSocketStore } from '@/lib/websocket';

/**
 * AgendaPanel — the "Next 24 Hours" surface.
 *
 * A pure consumer of the server-composed Agenda Engine (`GET /me/agenda`). It performs
 * NO date maths and NO business calculation — the server owns the rolling-24h window,
 * the today/tomorrow bucket and the time label (in chambers time). This component only
 * renders the one ordered timeline the API returns.
 *
 * Live: the `['me-agenda']` React Query key is invalidated by WebSocketProvider on every
 * calendar/hearing/task/matter change, and a lightweight poll backs it up so a dropped
 * socket still refreshes. So a reschedule reflects here with no manual reload.
 */

const KIND_ICON: Record<AgendaItemType, React.ElementType> = {
  hearing: Gavel,
  conference: ChatCircle,
  meeting: UsersThree,
  task: ListChecks,
  diary: CalendarBlank,
};

const KIND_TINT: Record<AgendaItemType, string> = {
  hearing: 'text-brand-600 dark:text-brand-400',
  conference: 'text-emerald-600 dark:text-emerald-400',
  meeting: 'text-sky-600 dark:text-sky-400',
  task: 'text-violet-500',
  diary: 'text-muted-foreground',
};

const DEFAULT_VISIBLE = 6;

function itemLink(item: AgendaItem, basePath: string): string {
  if (item.hearingId) return `${basePath}/hearings/${item.hearingId}`;
  if (item.type === 'task' && item.matterId) return `${basePath}/matters/${item.matterId}?tab=tasks`;
  if (item.matterId) return `${basePath}/matters/${item.matterId}`;
  return `${basePath}/diary`;
}

export function AgendaPanel({
  basePath = '/barrister',
  showBarrister = false,
}: {
  basePath?: string;
  /** Clerk view — a clerk sees several barristers, so name the owner on each row. */
  showBarrister?: boolean;
}) {
  // Event-driven refresh (the synchronization strategy): the ['me-agenda'] key is
  // invalidated by WebSocketProvider the instant a hearing/task/conference/diary/matter
  // changes (calendar:updated / matters:updated), so the panel refetches only when
  // something relevant actually changes — NO perpetual poll in the normal case.
  //
  // Polling here is DISASTER RECOVERY only: engaged solely while the live socket is
  // down (so a missed event can't leave the dashboard stale forever), and it stops the
  // moment the socket reconnects. refetchOnReconnect / refetchOnWindowFocus close the
  // gap immediately when the connection or tab comes back.
  const wsConnected = useWebSocketStore((s) => s.connected);
  const { data: items = [] } = useQuery({
    queryKey: ['me-agenda'],
    queryFn: fetchAgenda,
    staleTime: 30_000,
    refetchInterval: wsConnected ? false : 120_000, // fallback only when the socket is down
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const { visible, overflow } = useMemo(() => {
    return { visible: items.slice(0, DEFAULT_VISIBLE), overflow: Math.max(0, items.length - DEFAULT_VISIBLE) };
  }, [items]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Next 24 hours</span>
          <span className="text-xs font-normal text-muted-foreground">
            {items.length === 0 ? 'Nothing scheduled' : `${items.length} item${items.length > 1 ? 's' : ''}`}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            A clear next 24 hours — no hearings, conferences, meetings or tasks due.
          </p>
        ) : (
          <div className="space-y-0.5">
            {visible.map((item, i) => {
              const Icon = KIND_ICON[item.type];
              const prev = visible[i - 1];
              const showTomorrowDivider = item.dayLabel === 'tomorrow' && (!prev || prev.dayLabel !== 'tomorrow');
              const sub = item.type === 'task'
                ? (item.matterName ?? 'Task')
                : (item.matterName ?? item.location ?? null);
              return (
                <div key={item.id}>
                  {showTomorrowDivider && (
                    <div className="mb-1 mt-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Tomorrow
                    </div>
                  )}
                  <Link
                    to={itemLink(item, basePath)}
                    className="flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-muted/40"
                  >
                    <span className="w-14 shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                      {item.timeLabel}
                    </span>
                    <Icon className={`h-4 w-4 shrink-0 ${KIND_TINT[item.type]}`} weight="bold" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                      {item.title}
                    </span>
                    {showBarrister && item.barristerName && (
                      <span className="hidden shrink-0 text-xs text-muted-foreground md:block">
                        {item.barristerName}
                      </span>
                    )}
                    {sub && (
                      <span className="hidden min-w-0 max-w-[40%] shrink-0 items-center gap-1 truncate text-xs text-muted-foreground sm:flex">
                        {item.type !== 'task' && item.location && <MapPin className="h-3 w-3 shrink-0" />}
                        <span className="truncate">{sub}</span>
                      </span>
                    )}
                  </Link>
                </div>
              );
            })}

            {overflow > 0 && (
              <Link
                to={`${basePath}/diary`}
                className="mt-1 flex items-center gap-1.5 px-2 py-2 text-xs font-medium text-brand-600 dark:text-brand-400 transition hover:text-brand-700 dark:hover:text-brand-300"
              >
                View full agenda
                <span className="text-muted-foreground">· {overflow} more</span>
                <ArrowRight className="h-3.5 w-3.5" weight="bold" />
              </Link>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
